import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle, ArrowLeft, LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Public page at /delete-account. Satisfies the Google Play / App Store
 * requirement for a reachable account-deletion URL. Shows:
 *   - If logged in → the full deletion flow (same guards as the dialog)
 *   - If not logged in → a short "sign in to continue" with a link to /login
 *     that bounces back here after auth.
 */
export default function DeleteAccount() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = "Delete account · NoteLosh"; }, []);

  const canDelete = confirmText.trim() === "DELETE" && !busy;

  const submit = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      await api.delete("/auth/me", { data: { confirm: "DELETE" } });
      toast.success("Your account has been deleted");
      try {
        localStorage.removeItem("notelosh_token");
        localStorage.removeItem("notelosh_user");
      } catch { /* ignore */ }
      try { await logout(); } catch { /* ignore */ }
      navigate("/login", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not delete account";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F3F4F6] px-4 py-10">
      <div className="max-w-xl mx-auto">
        <Link
          to="/"
          data-testid="delete-account-back-link"
          className="inline-flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#F3F4F6] mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to NoteLosh
        </Link>

        <h1 className="font-heading text-3xl sm:text-4xl font-semibold flex items-center gap-2 text-[#F87171]">
          <AlertTriangle className="h-7 w-7" /> Delete your account
        </h1>
        <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">
          This permanently removes your NoteLosh account and everything you alone own.
          It can't be undone. Buyers who already bought any of your marketplace notes keep their copy.
        </p>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-sm text-[#9CA3AF]">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking your account…
          </div>
        ) : !user ? (
          <div className="mt-8 rounded-2xl border border-[#27272A] bg-[#121212] p-5">
            <p className="text-sm text-[#D1D5DB]">
              You need to be signed in to delete your account. Sign in on this device
              first, then come back here.
            </p>
            <Link
              to={`/login?return=${encodeURIComponent("/delete-account")}`}
              data-testid="delete-account-signin-link"
            >
              <Button
                data-testid="delete-account-signin-btn"
                className="mt-4 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white gap-1.5"
              >
                <LogIn className="h-4 w-4" /> Sign in to continue
              </Button>
            </Link>
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-[#27272A] bg-[#121212] p-5 space-y-5">
            <div className="text-xs text-[#9CA3AF]">
              Signed in as <span className="text-[#F3F4F6] font-medium">{user.email}</span>
            </div>

            <div className="rounded-xl border border-[#7F1D1D]/40 bg-[#1E0A0A] p-4 text-sm text-[#FCA5A5] space-y-2">
              <p className="font-medium text-[#FCA5A5]">What gets deleted</p>
              <ul className="text-[12.5px] leading-relaxed text-[#FCA5A5]/90 list-disc pl-4 space-y-0.5">
                <li>All your notes, links and uploaded files</li>
                <li>Your marketplace listings are removed from the shop (archived)</li>
                <li>Your reminders, votes, comments and reports</li>
                <li>Your push-notification devices and session</li>
                <li>Your profile and sign-in credentials</li>
              </ul>
              <p className="font-medium text-[#FCA5A5] pt-1">What stays</p>
              <ul className="text-[12.5px] leading-relaxed text-[#FCA5A5]/90 list-disc pl-4 space-y-0.5">
                <li>Buyers keep the notes they already bought from you</li>
                <li>Past sales / orders stay on the ledger for accounting</li>
              </ul>
              <p className="text-[12.5px] leading-relaxed text-[#FCA5A5]/90 pt-1">
                If your wallet still has money, cash out first — we won't let a balance vanish on delete.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="delete-confirm" className="text-xs text-[#9CA3AF]">
                Type <span className="text-[#F3F4F6] font-mono">DELETE</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                data-testid="delete-account-confirm-input"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="bg-[#1E1E1E] border-[#27272A] h-11 rounded-xl font-mono tracking-widest"
              />
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                data-testid="delete-account-cancel-btn"
                onClick={() => navigate("/app")}
                disabled={busy}
                variant="ghost"
                className="rounded-full text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
              >
                Cancel
              </Button>
              <Button
                type="button"
                data-testid="delete-account-submit-btn"
                onClick={submit}
                disabled={!canDelete}
                className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B] text-white gap-1.5 disabled:opacity-40"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete my account
              </Button>
            </div>
          </div>
        )}

        <p className="mt-6 text-[11px] text-[#6B7280]">
          Questions about data deletion? Reach out via the Privacy policy page.
        </p>
      </div>
    </div>
  );
}
